export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: "/admin/login",
    REFRESH: "/admin/refresh",
    LOGOUT: "/admin/logout",
  },

  DASHBOARD: {
    STATS: "/admin/dashboard/stats",
    NOTIFICATIONS: "/admin/dashboard/notifications",
  },

  MODES: {
    LIST: "/api/v1/modes",
    DETAIL: (slug: string) => `/api/v1/modes/${slug}`,
    ADMIN_ALL: "/api/v1/modes/admin/all",
    CREATE: "/api/v1/modes",
    UPDATE: (id: number) => `/api/v1/modes/${id}`,
    UPLOAD_LOGO: "/api/v1/modes/upload-logo",
    TOGGLE: (id: number) => `/api/v1/modes/${id}/toggle`,
    REORDER: "/api/v1/modes/reorder",
    DELETE: (id: number) => `/api/v1/modes/${id}`,
  },

  ACADEMY_ADMIN: {
    AREAS: "/api/v1/admin/academy/areas",
    CONCEPTS: "/api/v1/admin/academy/concepts",
    CONCEPT: (slug: string) => `/api/v1/admin/academy/concepts/${slug}`,
    ASSET_KINDS: "/api/v1/admin/academy/asset-kinds",
    VOCABULARY: "/api/v1/admin/academy/vocabulary",
    CONCEPT_STATUS: (slug: string) =>
      `/api/v1/admin/academy/concepts/${slug}/status`,
    ASSET_PAYLOAD: (id: number) =>
      `/api/v1/admin/academy/assets/${id}/payload`,
    CREATE_CONCEPT: "/api/v1/admin/academy/concepts",
    CREATE_ASSET: (slug: string) =>
      `/api/v1/admin/academy/concepts/${slug}/assets`,
    PREREQUISITES: (slug: string) =>
      `/api/v1/admin/academy/concepts/${slug}/prerequisites`,
    ASSET: (id: number) => `/api/v1/admin/academy/assets/${id}`,
    REORDER_ASSETS: (slug: string) =>
      `/api/v1/admin/academy/concepts/${slug}/assets/order`,
    AREA: (key: string) => `/api/v1/admin/academy/areas/${key}`,
    AREA_STATUS: (key: string) =>
      `/api/v1/admin/academy/areas/${key}/status`,
    STAGES: (areaKey: string) =>
      `/api/v1/admin/academy/areas/${areaKey}/stages`,
    STAGE: (areaKey: string, stageKey: string) =>
      `/api/v1/admin/academy/areas/${areaKey}/stages/${stageKey}`,
    MOVE_CONCEPT: (slug: string) =>
      `/api/v1/admin/academy/concepts/${slug}/move`,
  },

  USERS: {
    LIST: "/admin/users",
    DETAIL: (id: string) => `/admin/users/${id}`,
    DELETE: (id: number) => `/admin/users/${id}`,
  },

  PLAYERS: {
    LIST: "/api/v1/players",
    DETAIL: (id: number) => `/api/v1/players/${id}`,
    CREATE: "/api/v1/players",
    UPDATE: (id: number) => `/api/v1/players/${id}`,
    DELETE: (id: number) => `/api/v1/players/${id}`,
    ACTIVATE: (id: number) => `/api/v1/players/${id}/activate`,
    DEACTIVATE: (id: number) => `/api/v1/players/${id}/deactivate`,
    TEAMS: (id: number) => `/api/v1/players/${id}/teams`,
    UPLOAD_PHOTO: "/api/v1/upload/player-photo",
    CAREER_LIST: (id: number) => `/api/v1/players/${id}/career-entries`,
    CAREER_CREATE: (id: number) => `/api/v1/players/${id}/career-entries`,
    CAREER_UPDATE: (id: number, entryId: number) =>
      `/api/v1/players/${id}/career-entries/${entryId}`,
    CAREER_DELETE: (id: number, entryId: number) =>
      `/api/v1/players/${id}/career-entries/${entryId}`,
  },

  CLUBS: {
    LIST: "/api/v1/clubs",
    FILTERS: "/api/v1/clubs/filters",
    CREATE: "/api/v1/clubs",
    UPDATE: (id: number) => `/api/v1/clubs/${id}`,
    DELETE: (id: number) => `/api/v1/clubs/${id}`,
    UPLOAD_LOGO: "/api/v1/upload/club-logo",
  },

  QUIZ_TYPES: {
    PUBLIC_LIST: "/api/v1/quiz-types",
    ADMIN_LIST: "/api/v1/admin/quiz-types",
    CREATE: "/api/v1/admin/quiz-types",
    UPDATE: (id: number) => `/api/v1/admin/quiz-types/${id}`,
    TOGGLE: (id: number) => `/api/v1/admin/quiz-types/${id}/toggle`,
    DELETE: (id: number) => `/api/v1/admin/quiz-types/${id}`,
    REORDER: "/api/v1/admin/quiz-types/reorder",
    UPLOAD_ICON: "/api/v1/upload/quiz-type-icon",
  },

  NATIONS: {
    // Public (normal users)
    LIST: "/api/v1/nations",
    DETAIL: (id: number) => `/api/v1/nations/${id}`,

    // Admin only (same base path, guarded on backend)
    CREATE: "/api/v1/nations",
    UPDATE: (id: number) => `/api/v1/nations/${id}`,
    DELETE: (id: number) => `/api/v1/nations/${id}`,
    ELIMINATE: (id: number) => `/api/v1/nations/${id}/eliminate`,
    UPLOAD_FLAG: "/api/v1/nations/upload-flag",
  },

  MATCHES: {
    LIST: "/api/v1/admin/matches",
    DETAIL: (id: number) => `/api/v1/admin/matches/${id}`,
    CREATE: "/api/v1/admin/matches",
    UPDATE: (id: number) => `/api/v1/admin/matches/${id}`,
    DELETE: (id: number) => `/api/v1/admin/matches/${id}`,
    UPDATE_STATUS: (id: number) => `/api/v1/admin/matches/${id}/status`,
    LIVE: "/api/v1/admin/matches/live",
    UPCOMING: "/api/v1/admin/matches/upcoming",
  },

  EVENTS: {
    LIST: "/api/v1/admin/events",
    CREATE: "/api/v1/admin/events",
    DELETE: (id: number) => `/api/v1/admin/events/${id}`,
    MATCH_EVENTS: (matchId: number) => `/api/v1/admin/events/match/${matchId}`,
    RECENT: "/api/v1/admin/events/recent",
    BULK_APPEARANCES: "/api/v1/admin/events/bulk-appearances",
    CLEAN_SHEET: "/api/v1/admin/events/clean-sheet",
  },

  PHASES: {
    CURRENT: "/api/v1/admin/phases/current",
    ALL: "/api/v1/admin/phases/all",
    BRACKET: "/api/v1/admin/phases/bracket",
    ELIMINATED: "/api/v1/admin/phases/eliminated",
    ACTIVE: "/api/v1/admin/phases/active",
    ADVANCE: "/api/v1/admin/phases/advance",
    SIMULATE_ADVANCE: "/api/v1/admin/phases/simulate-advance",
  },
  // ─── ADD THIS to API_ENDPOINTS in src/api/endpoints.ts ───────────────────────

  GAMEWEEKS: {
    LIST: "/api/v1/admin/gameweeks",
    STATS: (id: number) => `/api/v1/admin/gameweeks/${id}/stats`,
    CREATE: "/api/v1/admin/gameweeks",
    UPDATE: (id: number) => `/api/v1/admin/gameweeks/${id}`,
    OPEN: (id: number) => `/api/v1/admin/gameweeks/${id}/open`,
    LOCK: (id: number) => `/api/v1/admin/gameweeks/${id}/lock`,
    CALCULATE_POINTS: (id: number) =>
      `/api/v1/admin/gameweeks/${id}/calculate-points`,
    ASSIGN_MATCHES: (id: number) =>
      `/api/v1/admin/gameweeks/${id}/assign-matches`,
    FORCE_RECALCULATE: (id: number) =>
      `/api/v1/admin/gameweeks/${id}/force-recalculate`,
    DELETE: (id: number) => `/api/v1/admin/gameweeks/${id}`,
    LEADERBOARD: (id: number) => `/api/v1/admin/gameweeks/${id}/leaderboard`,
    LEAGUE_LEADERBOARD: (gameweekId: number, leagueId: number) =>
      `/api/v1/admin/gameweeks/${gameweekId}/league/${leagueId}/leaderboard`,
    EXPORT_CSV: (id: number) =>
      `/api/v1/admin/gameweeks/${id}/export.csv`,
    EXPORT_ALL_CSV: "/api/v1/admin/gameweeks/export-all.csv",
    TEAM_OF_WEEK: (id: number) =>
      `/api/v1/admin/gameweeks/${id}/team-of-the-week`,
  },

  FANTASYLEADERBOARD: {
    GLOBAL: "/api/v1/admin/fantasy-leaderboard/global",
    LEAGUE: (id: number) => `/api/v1/admin/fantasy-leaderboard/leagues/${id}`,
    USER_RANK: (id: number) =>
      `/api/v1/admin/fantasy-leaderboard/users/${id}/rank`,
    STATS: "/api/v1/admin/fantasy-leaderboard/stats",
    TOP_PLAYERS: "/api/v1/admin/fantasy-leaderboard/top-players",
    TOP_CAPTAINS: "/api/v1/admin/fantasy-leaderboard/top-captains",
    EXPORT: "/api/v1/admin/fantasy-leaderboard/export",
    INVALIDATE_CACHE: "/api/v1/admin/fantasy-leaderboard/invalidate-cache",
    GAMEWEEK: (id: number) => `/api/v1/admin/fantasy-leaderboard/gameweek/${id}`,
    GAMEWEEK_WINNERS: (id: number) => `/api/v1/admin/fantasy-leaderboard/gameweek/${id}/winners`,
  },

  ANNOUNCEMENTS: {
    LIST: "/api/v1/admin/announcements",
    CREATE: "/api/v1/admin/announcements",
    UPDATE: (id: number) => `/api/v1/admin/announcements/${id}`,
    TOGGLE: (id: number) => `/api/v1/admin/announcements/${id}/toggle`,
    DELETE: (id: number) => `/api/v1/admin/announcements/${id}`,
  },

  NOTIFICATIONS: {
    BROADCAST: "/admin/notifications/broadcast",
    STATS: "/admin/notifications/stats",
  },

  ABUSE_REPORTS: {
    LIST: "/api/v1/admin/reports",
    RESOLVE: (id: number) => `/api/v1/admin/reports/${id}/resolve`,
  },

  TEAMS: {
    ADMIN_ALL: "/api/v1/teams/admin/all",
    ADMIN_DETAIL: (id: number) => `/api/v1/teams/admin/${id}`,
    ADMIN_UPDATE: (id: number) => `/api/v1/teams/admin/${id}`,
    ADMIN_DELETE: (id: number) => `/api/v1/teams/admin/${id}`,
  },

  LEAGUES: {
    LIST: "/api/v1/leagues/admin/all",
    DETAIL: (id: number) => `/api/v1/leagues/admin/${id}`,
    TEAMS: (id: number) => `/api/v1/leagues/admin/${id}/teams`,
    TEAM_PLAYERS: (teamId: number) => `/api/v1/leagues/admin/teams/${teamId}/players`,
    CREATE: "/api/v1/leagues/admin",
    UPDATE: (id: number) => `/api/v1/leagues/admin/${id}`,
    DELETE: (id: number) => `/api/v1/leagues/admin/${id}`,
  },

  QUIZ: {
    CAREER_PATH_PLAYERS: "/api/v1/admin/quiz/career-path/players",
    MCQ_LIST: "/api/v1/admin/quiz/mcq",
    MCQ_CREATE: "/api/v1/admin/quiz/mcq",
    MCQ_UPDATE: (id: number) => `/api/v1/admin/quiz/mcq/${id}`,
    MCQ_TOGGLE: (id: number) => `/api/v1/admin/quiz/mcq/${id}/toggle`,
    MCQ_DELETE: (id: number) => `/api/v1/admin/quiz/mcq/${id}`,
    WHO_AM_I_LIST: "/api/v1/admin/quiz/who-am-i",
    WHO_AM_I_CREATE: "/api/v1/admin/quiz/who-am-i",
    WHO_AM_I_UPDATE: (id: number) => `/api/v1/admin/quiz/who-am-i/${id}`,
    WHO_AM_I_TOGGLE: (id: number) => `/api/v1/admin/quiz/who-am-i/${id}/toggle`,
    WHO_AM_I_DELETE: (id: number) => `/api/v1/admin/quiz/who-am-i/${id}`,
    PLAYERS_LIST: "/api/v1/admin/quiz/players",
    PLAYERS_CREATE: "/api/v1/admin/quiz/players",
    PLAYERS_UPDATE: (id: number) => `/api/v1/admin/quiz/players/${id}`,
    PLAYERS_DELETE: (id: number) => `/api/v1/admin/quiz/players/${id}`,
    PLAYERS_TOGGLE: (id: number) => `/api/v1/admin/quiz/players/${id}/toggle`,
    // Higher or Lower — stat cards (Goals / Assists tabs)
    HOL_LIST: "/api/v1/admin/quiz/higher-or-lower",
    HOL_CREATE: "/api/v1/admin/quiz/higher-or-lower",
    HOL_UPDATE: (id: number) => `/api/v1/admin/quiz/higher-or-lower/${id}`,
    HOL_DELETE: (id: number) => `/api/v1/admin/quiz/higher-or-lower/${id}`,
    // Higher or Lower — transfer-fee cards (Transfers tab)
    HOL_TRANSFER_LIST: "/api/v1/admin/quiz/higher-or-lower/transfers",
    HOL_TRANSFER_CREATE: "/api/v1/admin/quiz/higher-or-lower/transfers",
    HOL_TRANSFER_UPDATE: (id: number) =>
      `/api/v1/admin/quiz/higher-or-lower/transfers/${id}`,
    HOL_TRANSFER_DELETE: (id: number) =>
      `/api/v1/admin/quiz/higher-or-lower/transfers/${id}`,
    // Mystery Club
    MYSTERY_LIST: "/api/v1/admin/quiz/mystery-club",
    MYSTERY_CREATE: "/api/v1/admin/quiz/mystery-club",
    MYSTERY_UPDATE: (id: number) => `/api/v1/admin/quiz/mystery-club/${id}`,
    MYSTERY_DELETE: (id: number) => `/api/v1/admin/quiz/mystery-club/${id}`,
    // True or False
    TF_LIST: "/api/v1/admin/quiz/true-or-false",
    TF_CREATE: "/api/v1/admin/quiz/true-or-false",
    TF_UPDATE: (id: number) => `/api/v1/admin/quiz/true-or-false/${id}`,
    TF_DELETE: (id: number) => `/api/v1/admin/quiz/true-or-false/${id}`,
    // Football Grid (Tic-Tac-Toe)
    TTT_LIST: "/api/v1/admin/quiz/tictactoe",
    TTT_CREATE: "/api/v1/admin/quiz/tictactoe",
    TTT_TOGGLE: (id: number) => `/api/v1/admin/quiz/tictactoe/${id}/toggle`,
    TTT_UPDATE: (id: number) => `/api/v1/admin/quiz/tictactoe/${id}`,
    TTT_DELETE: (id: number) => `/api/v1/admin/quiz/tictactoe/${id}`,
    TTT_CELL_ANSWERS: (cellId: number) =>
      `/api/v1/admin/quiz/tictactoe/cells/${cellId}/answers`,
    TTT_CELL_ANSWER_REMOVE: (cellId: number, playerId: number) =>
      `/api/v1/admin/quiz/tictactoe/cells/${cellId}/answers/${playerId}`,
    TTT_CELL_SUGGEST: (cellId: number) =>
      `/api/v1/admin/quiz/tictactoe/cells/${cellId}/suggest`,
    // Question Reports
    REPORTS_LIST: "/api/v1/admin/quiz/reports",
    REPORT_REVIEW: (id: number) => `/api/v1/admin/quiz/reports/${id}/review`,
  },

  LFS: {
    GAMES: "/api/v1/admin/lfs/games",
    GAME: (id: number) => `/api/v1/admin/lfs/games/${id}`,
    PARTICIPANTS: (gameId: number) => `/api/v1/admin/lfs/games/${gameId}/participants`,
    ROUNDS: (gameId: number) => `/api/v1/admin/lfs/games/${gameId}/rounds`,
    CREATE_ROUND: (gameId: number) => `/api/v1/admin/lfs/games/${gameId}/rounds`,
    DELETE_ROUND: (roundId: number) => `/api/v1/admin/lfs/rounds/${roundId}`,
    PROCESS_ROUND: (roundId: number) => `/api/v1/admin/lfs/rounds/${roundId}/process`,
    ROUND_PICKS: (roundId: number) => `/api/v1/admin/lfs/rounds/${roundId}/picks`,
    SUGGESTED_WINNERS: (roundId: number) => `/api/v1/admin/lfs/rounds/${roundId}/suggested-winners`,
    EXPORT_CSV: (gameId: number) => `/api/v1/admin/lfs/games/${gameId}/export.csv`,
  },

  DAILY_CHALLENGE: {
    LIST: "/api/v1/admin/daily-challenge",
    WEEK_STATUS: "/api/v1/admin/daily-challenge/week-status",
    CREATE: "/api/v1/admin/daily-challenge",
    UPDATE: (id: number) => `/api/v1/admin/daily-challenge/${id}`,
    DELETE: (id: number) => `/api/v1/admin/daily-challenge/${id}`,
    UPLOAD_IMAGE: "/api/v1/upload/daily-challenge-image",
  },
  STORIES: {
    LIST: "/api/v1/admin/stories",
    CREATE: "/api/v1/admin/stories",
    UPDATE: (id: number) => `/api/v1/admin/stories/${id}`,
    TOGGLE: (id: number) => `/api/v1/admin/stories/${id}/toggle`,
    DELETE: (id: number) => `/api/v1/admin/stories/${id}`,
    UPLOAD_IMAGE: "/api/v1/upload/story-image",
  },
  // api-football live ingestion admin tools
  API_FOOTBALL: {
    AUDIT: "/api/v1/admin/api-football/audit",
    INGEST: (matchId: number) =>
      `/api/v1/admin/api-football/ingest/${matchId}`,
    INGEST_ALL_POLLABLE: "/api/v1/admin/api-football/ingest-all-pollable",
    PREVIEW: (matchId: number) =>
      `/api/v1/admin/api-football/preview/${matchId}`,
    STATUS: "/api/v1/admin/api-football/status",
    TICK_NOW: "/api/v1/admin/api-football/tick-now",
  },

  ADMIN_TEAMS: {
    LIST: "/api/v1/admin/teams",
    DETAIL: (id: number) => `/api/v1/admin/teams/${id}`,
    SWAP: (id: number) => `/api/v1/admin/teams/${id}/swap`,
    CAPTAIN: (id: number) => `/api/v1/admin/teams/${id}/captain`,
    AUDIT: (id: number) => `/api/v1/admin/teams/${id}/audit`,
  },
} as const;
